import { LinkIcon, MicrophoneIcon, UploadIcon } from '@heroicons/react/outline';
import Link from 'next/link';

import styles from '@/components/agents/agents.module.css';

import clsxm from '@/lib/clsxm';

import {
  ArrowGlyph,
  delay,
  SectionHeading,
  Stage,
  tone,
} from '@/components/agents/primitives';
import { useReveal } from '@/components/agents/useReveal';
import { SourceIcon, type SourceKind } from '@/components/audio/audioUi';

import { FILE_FORMATS, useLandingT } from './kit';
import { TOOL_LINKS } from './pages';

/**
 * Where a recording can come from (upload, link, recording), then links to
 * the format and source tools, which also carry the pages' internal linking.
 */
export default function Sources({ currentPath }: { currentPath?: string }) {
  const { t } = useLandingT();
  const reveal = useReveal<HTMLDivElement>(0.15);

  const links: { kind: SourceKind; label: string }[] = [
    { kind: 'youtube', label: 'YouTube' },
    { kind: 'instagram', label: 'Instagram' },
    { kind: 'tiktok', label: 'TikTok' },
    { kind: 'googledrive', label: 'Google Drive' },
    { kind: 'link', label: t('Any public audio or video link') },
  ];

  const tools = TOOL_LINKS.filter((link) => link.path !== currentPath);

  return (
    <section className='py-20 sm:py-28'>
      <div className='mx-auto max-w-7xl px-6 lg:px-8'>
        <SectionHeading
          title={t('Bring any recording.')}
          tail={t('From anywhere.')}
          lead={t(
            'Upload a file from your computer or phone, paste a link, or record in your browser.',
          )}
        />
      </div>

      <div className='mx-auto mt-14 max-w-7xl px-3 sm:px-6 lg:px-8'>
        <Stage
          variant='lavender'
          halftoneAt='0% 100%'
          halftoneReach={55}
          className='px-6 py-10 sm:px-10 sm:py-12'
        >
          <div
            ref={reveal.ref}
            data-reveal={reveal.state}
            className={clsxm(
              styles.reveal,
              'grid gap-10 lg:grid-cols-3 lg:gap-0',
            )}
          >
            <div className={clsxm(styles.rv, 'lg:pe-10')} style={delay(0)}>
              <LaneTitle
                icon={<UploadIcon className='h-5 w-5' aria-hidden />}
                title={t('Upload a file')}
              />
              <ul className='mt-5 flex flex-wrap gap-1.5' role='list'>
                {FILE_FORMATS.map((format) => (
                  <li
                    key={format}
                    className={clsxm(
                      'rounded-lg bg-white px-2.5 py-1 font-mono text-[12px] font-semibold ring-1 ring-[#0f0f28]/[0.06] dark:bg-white/[0.06] dark:ring-white/10',
                      tone.ink,
                    )}
                  >
                    {format}
                  </li>
                ))}
                <li
                  className={clsxm(
                    'rounded-lg px-2.5 py-1 text-[12px] font-medium',
                    tone.body,
                  )}
                >
                  {t('and 25 more')}
                </li>
              </ul>
            </div>

            <div
              className={clsxm(
                styles.rv,
                'lg:border-s lg:border-[#0f0f28]/[0.08] lg:px-10 dark:lg:border-white/[0.08]',
              )}
              style={delay(100)}
            >
              <LaneTitle
                icon={<LinkIcon className='h-5 w-5' aria-hidden />}
                title={t('Paste a link')}
              />
              <ul className='mt-5 space-y-2.5' role='list'>
                {links.map((link) => (
                  <li
                    key={link.kind}
                    className={clsxm(
                      'flex items-center gap-3 text-[15px] font-medium',
                      tone.ink,
                    )}
                  >
                    <span className='flex h-8 w-8 items-center justify-center rounded-xl bg-white ring-1 ring-[#0f0f28]/[0.06] dark:bg-white/[0.06] dark:ring-white/10'>
                      <SourceIcon kind={link.kind} tile={false} />
                    </span>
                    {link.label}
                  </li>
                ))}
              </ul>
            </div>

            <div
              className={clsxm(
                styles.rv,
                'lg:border-s lg:border-[#0f0f28]/[0.08] lg:ps-10 dark:lg:border-white/[0.08]',
              )}
              style={delay(200)}
            >
              <LaneTitle
                icon={<MicrophoneIcon className='h-5 w-5' aria-hidden />}
                title={t('Record')}
              />
              <p className={clsxm('mt-5 text-[15px] leading-7', tone.body)}>
                {t(
                  'Record a meeting, a class or a voice note in your browser. The AI Note Taker records up to 2 hours and turns it into notes when you stop.',
                )}
              </p>
              {currentPath !== '/ai-note-taker' ? (
                <Link
                  href='/ai-note-taker'
                  className='group mt-5 inline-flex items-center gap-1.5 text-[15px] font-semibold text-primary-700 hover:text-primary-800 dark:text-primary-300 dark:hover:text-primary-200'
                >
                  {t('Meet the AI Note Taker')}
                  <ArrowGlyph className='h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-0.5 rtl:group-hover:-translate-x-0.5' />
                </Link>
              ) : null}
            </div>
          </div>
        </Stage>

        {tools.length ? (
          <nav
            aria-label={t('More transcription tools')}
            className='mt-8 flex flex-wrap items-center gap-2 px-3 sm:px-0'
          >
            <span className={clsxm('me-2 text-sm font-semibold', tone.ink)}>
              {t('More transcription tools')}
            </span>
            {tools.map((link) => (
              <Link
                key={link.path}
                href={link.path}
                className={clsxm(
                  'inline-flex items-center gap-1.5 rounded-full bg-white px-3.5 py-1.5 text-sm font-medium ring-1 ring-[#0f0f28]/[0.08] transition hover:text-primary-700 hover:ring-primary-300 dark:bg-white/[0.05] dark:ring-white/10 dark:hover:text-primary-300 dark:hover:ring-primary-400/40',
                  tone.ink,
                )}
              >
                {t(link.label)}
              </Link>
            ))}
          </nav>
        ) : null}
      </div>
    </section>
  );
}

function LaneTitle({ icon, title }: { icon: React.ReactNode; title: string }) {
  return (
    <h3
      className={clsxm(
        'flex items-center gap-2.5 text-lg font-semibold tracking-tight',
        tone.ink,
      )}
    >
      <span className='flex h-9 w-9 items-center justify-center rounded-xl bg-primary-600 text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.22)]'>
        {icon}
      </span>
      {title}
    </h3>
  );
}
